import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { devOpsApiRef } from '@internal/backstage-plugin-ewii-azure';
import { Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress, makeStyles, IconButton, Tooltip } from '@material-ui/core';
import { ExpandMore as ExpandMoreIcon, Link as LinkIcon, Close as CloseIcon, Visibility as VisibilityIcon } from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import { createMarkdownStyles, sanitizeMarkdownLinkUrls } from '@internal/backstage-plugin-ewii-azure';

const useStyles = makeStyles((theme) => ({
  accordion: {
    width: '100%',
    minWidth: 0,
    '&.MuiAccordion-root': {
      margin: 0,
      '&:before': {
        display: 'none',
      },
    },
    '&.MuiAccordion-root.Mui-expanded': {
      margin: 0,
    },
  },
  // Modal overlay styles for full row expansion
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1300,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '10vh',
    paddingBottom: '10vh',
    paddingLeft: '5vw',
    paddingRight: '5vw',
  },
  modalContent: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[24],
    maxWidth: '90vw',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  closeButton: {
    padding: theme.spacing(1),
  },
  accordionSummary: {
    minHeight: 'auto',
    padding: theme.spacing(0.5, 1),
    '&.MuiAccordionSummary-root': {
      minHeight: 'auto',
    },
    '&.MuiAccordionSummary-root.Mui-expanded': {
      minHeight: 'auto',
    },
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0, // Allow shrinking
  },
  displayText: {
    flex: 1,
    minWidth: 0, // Allow text to shrink
    marginRight: theme.spacing(1),
  },
  copyButton: {
    padding: theme.spacing(0.5),
    flexShrink: 0, // Don't shrink the button
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  accordionDetails: {
    padding: theme.spacing(1),
    display: 'block',
    width: '100%',
  },
  markdownContent: createMarkdownStyles(theme),
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2.5),
    gap: theme.spacing(1),
  },
  errorContainer: {
    padding: theme.spacing(1.25),
    color: theme.palette.error.main,
  },
  contentContainer: {
    width: '100%',
    maxHeight: '400px',
    overflow: 'auto',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

interface ChangeLogModalProps {
  component: string;
  fileName: string;
  displayText?: string;
  fullRowExpansion?: boolean;
}

// Utility function to capitalize first letter
const capitalize = (str: string): string => 
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

export const ChangeLogModal: React.FC<ChangeLogModalProps> = ({ 
  component, 
  fileName, 
  displayText = "View Change Log",
  fullRowExpansion = false
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [componentMetadata, setComponentMetadata] = useState<{
    title: string;
    lifecycle: string;
  }>({ title: '', lifecycle: '' });
  
  const devOpsApi = useApi(devOpsApiRef);
  const catalogApi = useApi(catalogApiRef);

  const changeLogUrl = useMemo(
    () => `${window.location.origin}/catalog/default/component/${component}/change-log/${fileName}`,
    [component, fileName]
  );

  const modalTitle = useMemo(() => {
    const title = componentMetadata.title || component;
    const lifecycle = componentMetadata.lifecycle ? ` - ${capitalize(componentMetadata.lifecycle)}` : '';
    return `Change Log - ${title}${lifecycle}`;
  }, [componentMetadata, component]);

  // Fetch component metadata
  const fetchComponentMetadata = useCallback(async () => {
    if (componentMetadata.title) return; // Already loaded
    
    try {
      const entity = await catalogApi.getEntityByRef(`component:default/${component}`);
      if (entity) {
        setComponentMetadata({
          title: entity.metadata.title || entity.metadata.name,
          lifecycle: entity.spec?.lifecycle as string || '',
        });
      }
    } catch (err) {
      setComponentMetadata({
        title: component,
        lifecycle: '',
      });
    }
  }, [catalogApi, component, componentMetadata.title]);

  // Load markdown content
  const loadMarkdown = useCallback(async () => {
    if (hasLoaded) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const content = await devOpsApi.getChangeLogMarkdown(component, fileName);
      setMarkdown(content);
      setHasLoaded(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load change log');
    } finally {
      setLoading(false);
    }
  }, [devOpsApi, component, fileName, hasLoaded]);

  const handleOpen = useCallback(async () => {
    setExpanded(true);
    await Promise.all([
      fetchComponentMetadata(),
      loadMarkdown(),
    ]);
  }, [fetchComponentMetadata, loadMarkdown]);

  const handleClose = useCallback(() => {
    setExpanded(false);
  }, []);

  const handleExpansionChange = useCallback(async (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    if (isExpanded) {
      await handleOpen();
    } else {
      handleClose();
    }
  }, [handleOpen, handleClose]);

  const handleCopyLink = useCallback(async (event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(changeLogUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }, [changeLogUrl]);

  // Auto-reset copy success state
  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => setCopySuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  if (!fileName) {
    return <Typography variant="body2" color="textSecondary">N/A</Typography>;
  }

  // Shared content component
  const ContentDisplay = () => (
    <>
      {loading && (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
          <Typography variant="body2">Loading change log...</Typography>
        </div>
      )}
      {error && (
        <Typography variant="body2" className={classes.errorContainer}>
          Error: {error}
        </Typography>
      )}
      {hasLoaded && !loading && !error && (
        <div className={classes.markdownContent}>
          <ReactMarkdown>{sanitizeMarkdownLinkUrls(markdown)}</ReactMarkdown>
        </div>
      )}
    </>
  );

  // Copy link button component
  const CopyLinkButton = ({ className = classes.copyButton }: { className?: string }) => (
    <Tooltip title={copySuccess ? "Link copied!" : "Copy link to change log"} arrow>
      <IconButton
        onClick={handleCopyLink}
        size="small"
        className={className}
        color={copySuccess ? "primary" : "default"}
      >
        <LinkIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  if (fullRowExpansion) {
    const modalContent = expanded ? (
      <div className={classes.modalOverlay} onClick={handleClose}>
        <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <Typography variant="h6">{modalTitle}</Typography>
            <div className={classes.buttonGroup}>
              <CopyLinkButton />
              <IconButton onClick={handleClose} size="small" className={classes.copyButton}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
          <div className={classes.modalBody}>
            <ContentDisplay />
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        <div className={classes.buttonGroup}>
          <IconButton
            onClick={handleOpen}
            size="small"
            className={classes.copyButton}
            title={displayText}
          >
            <Typography variant="body2" style={{ marginRight: 4 }}>
              {displayText}
            </Typography>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <CopyLinkButton />
        </div>
        {modalContent && createPortal(modalContent, document.body)}
      </>
    );
  }

  return (
    <Accordion 
      expanded={expanded} 
      onChange={handleExpansionChange}
      className={classes.accordion}
      elevation={0}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.accordionSummary}
      >
        <div className={classes.accordionHeader}>
          <Typography variant="body2" className={classes.displayText} noWrap>
            {displayText}
          </Typography>
          <CopyLinkButton />
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {expanded && (
          <div className={classes.contentContainer}>
            <ContentDisplay />
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
}; 